import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { useFocusTrap, useScrollLock } from "../lib/hooks";
import { sevLabel } from "../lib/format";

const cx = (...c) => c.filter(Boolean).join(" ");

// ── Surfaces ─────────────────────────────────────────────────────
export function Card({ title, subtitle, icon, actions, children, className, bodyClass, flush, footer, as: Tag = "section", ...rest }) {
  const hasHead = title || actions;
  return (
    <Tag className={cx("card", className)} {...rest}>
      {hasHead && (
        <header className="card-head">
          <div className="card-title-wrap">
            {icon && (
              <span className="card-icon">
                <Icon name={icon} size={16} />
              </span>
            )}
            <div className="card-titles">
              {title && <h3 className="card-title">{title}</h3>}
              {subtitle && <p className="card-sub">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className={cx(flush ? "card-flush" : "card-body", bodyClass)}>{children}</div>
      {footer && <footer className="card-foot">{footer}</footer>}
    </Tag>
  );
}

export function Section({ title, description, actions, children, className }) {
  return (
    <section className={cx("section", className)}>
      {(title || actions) && (
        <div className="section-head">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {actions && <div className="section-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// ── Badges & indicators ─────────────────────────────────────────
export function Badge({ tone = "neutral", children, dot, size, className, title }) {
  return (
    <span className={cx("badge", `tone-${tone}`, size === "lg" && "badge-lg", className)} title={title}>
      {dot && <i className="badge-dot" />}
      {children}
    </span>
  );
}

export function SevBadge({ sev, size }) {
  const s = String(sev || "INFO").toUpperCase();
  return (
    <span className={cx("sev-tag", `tone-${s.toLowerCase()}`, size === "lg" && "sev-tag-lg")}>
      <i aria-hidden="true" />
      {sevLabel(s)}
    </span>
  );
}

export function LiveDot({ tone = "ok", pulse = true, label }) {
  return (
    <span className={cx("live-dot", `tone-${tone}`, pulse && "is-pulsing")} role={label ? "img" : undefined} aria-label={label}>
      <i />
    </span>
  );
}

export function CountryTag({ code, name }) {
  const c = code && code !== "XX" ? code : "--";
  return (
    <span className="country">
      <span className="country-code">{c}</span>
      {name && <span className="country-name">{name}</span>}
    </span>
  );
}

// ── Buttons ─────────────────────────────────────────────────────
export const Button = React.forwardRef(function Button(
  { variant = "secondary", size = "md", icon, iconRight, loading, children, className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx("btn", `btn-${variant}`, `btn-${size}`, loading && "is-loading", className)}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={14} /> : icon && <Icon name={icon} size={size === "sm" ? 14 : 16} />}
      {children && <span>{children}</span>}
      {iconRight && !loading && <Icon name={iconRight} size={size === "sm" ? 14 : 16} />}
    </button>
  );
});

export function IconButton({ icon, label, size = 18, className, active, ...rest }) {
  return (
    <button type="button" className={cx("icon-btn", active && "is-active", className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

export function LinkButton({ href, variant = "secondary", size = "md", icon, children, className, ...rest }) {
  return (
    <a href={href} className={cx("btn", `btn-${variant}`, `btn-${size}`, className)} {...rest}>
      {icon && <Icon name={icon} size={size === "sm" ? 14 : 16} />}
      <span>{children}</span>
    </a>
  );
}

// ── Navigation controls ─────────────────────────────────────────
export function Tabs({ tabs, value, onChange, label = "Views", className }) {
  const refs = useRef({});
  const onKey = (e, i) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    let n = i;
    if (e.key === "ArrowRight") n = (i + 1) % tabs.length;
    if (e.key === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length;
    if (e.key === "Home") n = 0;
    if (e.key === "End") n = tabs.length - 1;
    onChange(tabs[n].id);
    refs.current[tabs[n].id]?.focus();
  };
  return (
    <div className={cx("tabs", className)} role="tablist" aria-label={label}>
      {tabs.map((t, i) => {
        const on = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => (refs.current[t.id] = el)}
            role="tab"
            type="button"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className={cx("tab", on && "is-active")}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.icon && <Icon name={t.icon} size={15} />}
            <span>{t.label}</span>
            {t.count != null && <span className="tab-count">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Segmented({ options, value, onChange, size = "md", label }) {
  return (
    <div className={cx("seg", size === "sm" && "seg-sm")} role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const opt = typeof o === "string" ? { id: o, label: o } : o;
        const on = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={on}
            className={cx("seg-item", on && "is-active")}
            onClick={() => onChange(opt.id)}
          >
            {opt.dot && <i className="seg-dot" style={{ background: opt.dot }} />}
            {opt.label}
            {opt.count != null && <span className="seg-count">{opt.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Chip({ active, onClick, children, tone, count }) {
  return (
    <button type="button" className={cx("chip", active && "is-active", tone && `tone-${tone}`)} aria-pressed={!!active} onClick={onClick}>
      {children}
      {count != null && <span className="chip-count">{count}</span>}
    </button>
  );
}

// ── States ──────────────────────────────────────────────────────
export function Spinner({ size = 16 }) {
  return <span className="spinner" style={{ width: size, height: size }} role="status" aria-label="Loading" />;
}

export function Empty({ icon = "shield", title, children, action, compact, tone }) {
  return (
    <div className={cx("empty", compact && "empty-compact", tone && `tone-${tone}`)}>
      <span className="empty-icon">
        <Icon name={icon} size={compact ? 18 : 22} />
      </span>
      {title && <strong>{title}</strong>}
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ title = "Couldn't load this view", detail, onRetry }) {
  return (
    <Empty icon="alertCircle" title={title} tone="critical" action={onRetry && <Button size="sm" icon="refresh" onClick={onRetry}>Try again</Button>}>
      {detail || "The backend did not respond. Check that the API service is running."}
    </Empty>
  );
}

export function Skeleton({ h = 14, w = "100%", r, className, style }) {
  return <span className={cx("skeleton", className)} style={{ height: h, width: w, borderRadius: r, ...style }} aria-hidden="true" />;
}

export function SkeletonRows({ rows = 5, h = 16 }) {
  return (
    <div className="skeleton-rows" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} h={h} w={`${92 - ((i * 13) % 30)}%`} />
      ))}
    </div>
  );
}

// ── Data display ────────────────────────────────────────────────
export function Meter({ value = 0, max = 100, tone = "brand", height = 6, label, color }) {
  const p = Math.max(0, Math.min(100, (value / (max || 1)) * 100));
  return (
    <div
      className={cx("meter", `tone-${tone}`)}
      style={{ height }}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      aria-label={label}
    >
      <i style={{ width: `${p}%`, background: color }} />
    </div>
  );
}

export function KV({ items, cols = 2, className }) {
  return (
    <dl className={cx("kv", className)} style={{ "--kv-cols": cols }}>
      {items.filter(Boolean).map(([k, v, opts = {}]) => (
        <div key={k} className="kv-item">
          <dt>{k}</dt>
          <dd className={cx(opts.mono && "mono", opts.wrap && "wrap")} style={opts.color ? { color: opts.color } : undefined}>
            {v == null || v === "" ? "—" : v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function Sparkline({ data = [], color = "var(--brand)", height = 32, fill = true, strokeWidth = 1.6 }) {
  const id = useId().replace(/:/g, "");
  if (!data.length) return <div style={{ height }} />;
  const w = 100;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const span = max - min || 1;
  const pts = data.map((v, i) => [(i / Math.max(data.length - 1, 1)) * w, height - 2 - ((v - min) / span) * (height - 4)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(" ");
  return (
    <svg className="sparkline" viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ height }} aria-hidden="true">
      <defs>
        <linearGradient id={`sg${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.28 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      {fill && <path d={`${line} L${w},${height} L0,${height} Z`} fill={`url(#sg${id})`} />}
      <path d={line} style={{ fill: "none", stroke: color }} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

export function Stat({ label, value, sub, icon, tone = "brand", spark, sparkColor, onClick, hint }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className={cx("stat", onClick && "is-clickable")} onClick={onClick} type={onClick ? "button" : undefined} title={hint}>
      <div className="stat-top">
        <span className="stat-label">{label}</span>
        {icon && (
          <span className={cx("stat-icon", `tone-${tone}`)}>
            <Icon name={icon} size={15} />
          </span>
        )}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-foot">
        {sub && <span className="stat-sub">{sub}</span>}
        {spark && (
          <span className="stat-spark">
            <Sparkline data={spark} color={sparkColor || `var(--${tone === "brand" ? "brand" : tone === "ok" ? "ok" : `sev-${tone}`})`} height={26} />
          </span>
        )}
      </div>
    </Tag>
  );
}

export function Callout({ tone = "info", icon = "info", title, children, className }) {
  return (
    <div className={cx("callout", `tone-${tone}`, className)}>
      <Icon name={icon} size={16} />
      <div>
        {title && <strong>{title}</strong>}
        {children && <div className="callout-body">{children}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, children, htmlFor, className }) {
  return (
    <label className={cx("field", className)} htmlFor={htmlFor}>
      {label && <span className="field-label">{label}</span>}
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function SearchInput({ value, onChange, placeholder, label, onClear, autoFocus, inputRef }) {
  return (
    <div className="search-input">
      <Icon name="search" size={16} />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
        autoFocus={autoFocus}
        spellCheck={false}
      />
      {value && (
        <button type="button" className="search-clear" onClick={() => (onClear ? onClear() : onChange(""))} aria-label="Clear search">
          <Icon name="x" size={14} />
        </button>
      )}
    </div>
  );
}

// ── Overlays ────────────────────────────────────────────────────
export function Drawer({ open, onClose, title, subtitle, icon, children, footer, width = 560, label, headerExtra }) {
  const ref = useRef(null);
  useFocusTrap(ref, open, onClose);
  useScrollLock(open);
  if (!open) return null;
  return createPortal(
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside ref={ref} className="drawer" style={{ "--drawer-w": `${width}px` }} role="dialog" aria-modal="true" aria-label={label || title} tabIndex={-1}>
        <header className="drawer-head">
          {icon && <span className="drawer-icon">{icon}</span>}
          <div className="drawer-titles">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {headerExtra}
          <IconButton icon="x" label="Close panel" onClick={onClose} />
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-foot">{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

export function Modal({ open, onClose, title, children, width = 440, label }) {
  const ref = useRef(null);
  useFocusTrap(ref, open, onClose);
  useScrollLock(open);
  if (!open) return null;
  return createPortal(
    <div className="overlay overlay-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className="modal" style={{ "--modal-w": `${width}px` }} role="dialog" aria-modal="true" aria-label={label || title} tabIndex={-1}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ── Toasts ──────────────────────────────────────────────────────
const ToastCtx = createContext(() => {});

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((msg, tone = "ok", opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    setItems((s) => [...s.slice(-3), { id, msg, tone, ...opts }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), opts.duration || 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx("toast", `tone-${t.tone}`)}>
            <Icon name={t.tone === "critical" ? "alertCircle" : t.tone === "medium" ? "alert" : "checkCircle"} size={17} />
            <div>
              <strong>{t.msg}</strong>
              {t.detail && <p>{t.detail}</p>}
            </div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

// ── Misc ────────────────────────────────────────────────────────
export function Kbd({ children }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function CopyButton({ value, label = "Copy" }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => setDone(false), 1400);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <IconButton
      icon={done ? "check" : "copy"}
      size={14}
      label={done ? "Copied" : label}
      className="icon-btn-sm"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(String(value)).then(() => setDone(true)).catch(() => {});
      }}
    />
  );
}

export { cx };
